import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../cart/CartContext";
import { useAuth } from "../auth/AuthContext";
import {
  FaBars, FaTimes, FaShoppingCart, FaHome, FaChevronDown, FaUserCircle,
} from "react-icons/fa";
import "../styles/DesignPage.css";

// Fallback images bundled (used only if CMS has none)
import img1 from "../assets/design/1.jpeg";
import img2 from "../assets/design/2.jpeg";
import img3 from "../assets/design/3.jpeg";
import img4 from "../assets/design/4.jpeg";

const fallback = {
  hero: {
    title: "Design & Decoration",
    subtitle:
      "Stunning floral arrangements, luxury lighting, and themed décor for unforgettable celebrations.",
    image: img1,
  },
  package: {
    id: "design-001",
    name: "Design & Decoration",
    description:
      "Full decor direction: florals, lighting, stage/mandap, and theme execution.",
    price: 20000,
    price_label: "",
    status: "available",
    image: img1,
  },
  gallery: [{ image: img1 }, { image: img2 }, { image: img3 }, { image: img4 }],
  features: [
    { title: "Elegant Florals", desc: "Bespoke bouquets, table décor, and entries with seasonal stems." },
    { title: "Atmospheric Lighting", desc: "Fairy lights, chandeliers, and creative up-lighting." },
    { title: "Theme Styling", desc: "From royal palaces to boho chic — full theme execution." },
    { title: "Stage & Mandap", desc: "Custom backdrops and mandap designs." }
  ],
  faqs: [
    { q: "Can I get a custom theme?", a: "Absolutely. Share your inspiration; we’ll create mood boards & mockups." },
    { q: "Do you provide setup & teardown?", a: "Yes, our team manages complete setup and pack-up." },
    { q: "Can you work with venue restrictions?", a: "We coordinate with the venue to meet all policies safely." }
  ],
};

function DesignPage() {
  const { addToCart, cart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dropdown, setDropdown] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const dropdownTimeout = useRef(null);

  // CMS data
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);

  const handleProfileClick = (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
    } else {
      navigate("/profile");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/content/design.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled && json) setData({ ...fallback, ...json });
      } catch (e) {
        console.warn("Using fallback design content:", e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hero = data?.hero || fallback.hero;
  const pack = data?.package || fallback.package;
  const gallery = Array.isArray(data?.gallery) && data.gallery.length ? data.gallery : fallback.gallery;
  const features = Array.isArray(data?.features) ? data.features : fallback.features;
  const faqs = Array.isArray(data?.faqs) ? data.faqs : fallback.faqs;

  const isAvailable = (pack?.status || "available") !== "unavailable";
  const hasNumericPrice = typeof pack?.price === "number" && pack.price > 0;
  const priceDisplay = hasNumericPrice ? `₹${pack.price.toLocaleString()}` : (pack?.price_label || "Custom Quote");

  const servicesLinks = [
    { to: "/wedding", label: "Wedding Planning" },
    { to: "/design", label: "Design & Decoration" },
    { to: "/destination", label: "Destination Wedding Venues" },
    { to: "/entertainment", label: "Entertainment" },
    { to: "/others", label: "Custom Events" },
  ];

  // Scroll reveal
  useEffect(() => {
    const sections = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver((ents) => {
      ents.forEach((e) => e.isIntersecting && e.target.classList.add("active"));
    }, { threshold: 0.15 });
    sections.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [loading]);

  const handleAdd = () => {
    if (!user) {
      alert("Please login to add items to cart");
      navigate("/");
      return;
    }
    if (!isAvailable || !hasNumericPrice) return;
    addToCart({
      id: pack.id,
      name: pack.name,
      price: pack.price,
      description: pack.description,
      image: pack.image || hero.image,
    });
    navigate("/cart");
  };

  return (
    <div className="design-page">
      {/* NAVBAR */}
      <header className="desktop-navbar">
        <div className="navbar-container">
          <div className="navbar-brand" onClick={() => navigate("/")}>Parivar</div>

          <nav className="center-nav">
            <Link to="/" className="nav-link"><FaHome /> Home</Link>

            <div
              className="dropdown"
              onMouseEnter={() => { clearTimeout(dropdownTimeout.current); setDropdown(true); }}
              onMouseLeave={() => { dropdownTimeout.current = setTimeout(() => setDropdown(false), 200); }}
            >
              <button className="dropdown-btn">Services <FaChevronDown /></button>
              {dropdown && (
                <div className="dropdown-menu animate-dropdown show">
                  {servicesLinks.map((item) => (
                    <Link key={item.to} to={item.to} className="dropdown-link" onClick={() => setDropdown(false)}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="nav-right">
            <a href="/profile" onClick={handleProfileClick} className="cart-link" aria-label="Profile"><FaUserCircle size={18} /></a>
            <Link to="/cart" className="cart-link" aria-label="Cart">
              <FaShoppingCart size={18} />
              {cart.length > 0 && <span className="cart-badge">{cart.length}</span>}
            </Link>
          </div>

          <div className="mobile-menu-icon" onClick={() => setNavOpen(true)}><FaBars size={22} /></div>
        </div>
      </header>

      {/* MOBILE MENU */}
      {navOpen && (
        <div className="sidebar-overlay" onClick={() => setNavOpen(false)}>
          <div className="sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="sidebar-header"><span>Menu</span><FaTimes onClick={() => setNavOpen(false)} /></div>
            <div className="sidebar-link" onClick={() => { navigate("/"); setNavOpen(false); }}><FaHome /> Home</div>
            <div className="sidebar-link" onClick={() => setDropdown(!dropdown)}><FaChevronDown /> Services</div>
            {dropdown && (
              <div className="sidebar-submenu">
                {servicesLinks.map((link) => (
                  <div key={link.to} className="sidebar-submenu-item" onClick={() => { navigate(link.to); setNavOpen(false); }}>
                    {link.label}
                  </div>
                ))}
              </div>
            )}
            <div className="sidebar-link" onClick={() => { 
              if (!user) {
                navigate("/login");
              } else {
                navigate("/profile");
              }
              setNavOpen(false); 
            }}><FaUserCircle /> Profile</div>
            <div className="sidebar-link" onClick={() => { navigate("/cart"); setNavOpen(false); }}>
              <FaShoppingCart /> Cart {cart.length > 0 && <span className="cart-badge">{cart.length}</span>}
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="hero reveal">
        <div className="hero-text">
          <h1>
            {hero.title}
            <span className={`pkg-status ${isAvailable ? "available" : "unavailable"}`}>
              {isAvailable ? "Available" : "Unavailable"}
            </span>
          </h1>
          <p>{hero.subtitle}<span>We transform every space into a storybook.</span></p>

          <div className="hero-cta">
            <button
              onClick={isAvailable && hasNumericPrice ? handleAdd : undefined}
              className={`primary-btn ${!isAvailable || !hasNumericPrice ? "disabled" : ""}`}
              disabled={!isAvailable || !hasNumericPrice}
              title={!isAvailable ? "Currently unavailable" : !hasNumericPrice ? "Request a custom quote" : "Add to cart"}
            >
              {isAvailable
                ? (hasNumericPrice ? `Add Full Decor Package ${priceDisplay}` : "Request Quote")
                : "Unavailable"}
            </button>
            {!hasNumericPrice && isAvailable && (
              <button className="outline-btn" onClick={() => navigate("/others")}>Request a Quote</button>
            )}
          </div>
        </div>

        <div className="hero-img">
          <img src={hero.image || pack.image} alt={pack.name} />
        </div>
      </section>

      {/* GALLERY */}
      <section className="gallery-section reveal">
        <h2>Gallery Highlights</h2>
        <div className="gallery-grid">
          {gallery.map((g, idx) => (
            <img key={idx} src={g.image} alt={g.alt || `Gallery ${idx + 1}`} loading="lazy" />
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="features-section reveal">
        <h2>Our Signature Touch</h2>
        <div className="features-grid">
          {features.map((f, i) => (
            <div key={i} className="feature-card">
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section reveal">
        <h2>FAQ</h2>
        {faqs.map((faq, i) => (
          <details key={i}>
            <summary>{faq.q}</summary>
            <p>{faq.a}</p>
          </details>
        ))}
      </section>
    </div>
  );
}

export default DesignPage;
